import { type LucideIcon, AlertTriangle } from 'lucide-react';
import { useAppTranslations } from '@/hooks/useAppTranslations';

type Domain = 'golf' | 'people' | 'admin';
type StateType = 'first-run' | 'no-results' | 'filtered' | 'error';

interface EmptyStateAction {
  label: string;
  onClick?: () => void;
  href?: string;
}

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description?: string;
  action?: EmptyStateAction;
  secondaryAction?: EmptyStateAction;
  domain?: Domain;
  stateType?: StateType;
  isError?: boolean;
  className?: string;
}

const domainStyles: Record<Domain, { ring: string; icon: string; btn: string }> = {
  golf: {
    ring: 'bg-gradient-to-br from-emerald-100 to-green-200',
    icon: 'text-[#1B4332]',
    btn: 'bg-[linear-gradient(135deg,#52B788_0%,#2D5A3D_50%,#1B4332_100%)]',
  },
  people: {
    ring: 'bg-gradient-to-br from-[#2A4494] to-[#1E3270]',
    icon: 'text-white',
    btn: 'bg-[linear-gradient(135deg,#2A4494_0%,#1E3270_100%)]',
  },
  admin: {
    ring: 'bg-gradient-to-br from-slate-100 to-slate-200',
    icon: 'text-slate-700',
    btn: 'bg-[linear-gradient(135deg,#475569_0%,#1e293b_100%)]',
  },
};

const errorStyles = {
  ring: 'bg-gradient-to-br from-amber-100 to-yellow-200',
  icon: 'text-amber-700',
  btn: 'bg-[linear-gradient(135deg,#F59E0B_0%,#B45309_100%)]',
};

function ActionButton({
  action,
  className,
}: {
  action: EmptyStateAction;
  className: string;
}) {
  const props = action.href
    ? { as: 'a' as const, href: action.href }
    : { as: 'button' as const, onClick: action.onClick, type: 'button' as const };

  if (props.as === 'a') {
    return (
      <a
        href={props.href}
        className={className}
      >
        {action.label}
      </a>
    );
  }
  return (
    <button type="button" onClick={action.onClick} className={className}>
      {action.label}
    </button>
  );
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  secondaryAction,
  domain = 'golf',
  stateType,
  isError = false,
  className = '',
}: EmptyStateProps) {
  const { t } = useAppTranslations();

  const isErrorState = isError || stateType === 'error';
  const styles = isErrorState ? errorStyles : domainStyles[domain];

  // Error state overrides: swap icon to AlertTriangle, provide fallback copy
  const DisplayIcon = isErrorState ? AlertTriangle : Icon;
  const displayTitle = isErrorState && !title ? t.common.emptyState.couldNotLoad : title;
  const displayDescription =
    isErrorState && !description ? t.common.emptyState.somethingWentWrong : description;

  return (
    <div
      className={`bg-white border border-slate-200 rounded-[20px] px-7 py-10 text-center shadow-sm ${className}`}
    >
      {/* Icon ring */}
      <div
        className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 relative ${styles.ring}`}
        style={{
          boxShadow: isErrorState
            ? '0 0 0 12px rgba(245,158,11,0.08)'
            : domain === 'people'
            ? '0 0 0 12px rgba(42,68,148,0.08)'
            : '0 0 0 12px rgba(82,183,136,0.08)',
        }}
      >
        <DisplayIcon
          size={44}
          strokeWidth={1.75}
          className={styles.icon}
        />
      </div>

      {/* Copy */}
      <h3 className="text-[20px] font-extrabold text-slate-900 leading-tight tracking-tight mb-2.5">
        {displayTitle}
      </h3>
      {displayDescription && (
        <p className="text-sm text-slate-500 leading-relaxed mb-7">{displayDescription}</p>
      )}

      {/* Actions */}
      {action && (
        <div className="flex flex-col gap-2.5">
          <ActionButton
            action={action}
            className={`${styles.btn} text-white font-bold text-[15px] tracking-tight py-3.5 px-6 rounded-[14px] w-full border-none cursor-pointer`}
          />
          {secondaryAction && (
            <ActionButton
              action={secondaryAction}
              className="bg-transparent border border-slate-200 text-slate-500 font-semibold text-sm py-3 px-6 rounded-[14px] w-full cursor-pointer hover:border-slate-300 hover:text-slate-700 transition-colors"
            />
          )}
        </div>
      )}
    </div>
  );
}
